import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, Button, styled } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import TopMenu from "components/UI/TopMenu";
import colors, { sizes } from "theme/themeConstants";
import default_user_image from "components/icons/default_user_image.png";
import { fetchCurrentUser, updateProfilePhoto, updateUserProfile } from "store/user/user-action";
import { IAppState } from "store/app-state";
import CustomInput from "components/UI/CustomInput";
import { IOperation } from "services/RedGifsService";
import { PROFILE_UPDATE_FIELDS_PATHS } from "utils/constants";
import { FooterAds } from "../../components/ads/FooterAds";
import { SocialLinksForm } from "pages/VerifyPage/SocialLinksForm";
import { IInput } from "pages/VerifyPage/VerifyFrom";
import { v4 as uuidv4 } from "uuid";
import { socialAdapter } from "components/profile/CreatorInfo";
import "./styles/ProfileSettingsPage.scss";
import { BackButton } from "components/buttons";
import { routes } from "routes";
import { LINK_REGEXP } from "pages/VerifyPage/constants";

const MOCK_TOPIC = ["Account Info", "Profile"];

export const ProfileSettingsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [inputList, setInputList] = useState<IInput[]>([]);
  const [socialTabs, setSocialTabs] = useState<any[]>(Object.entries(LINK_REGEXP));

  const currentUser = useSelector((state: IAppState) => state.user.currentUser);

  const { handleSubmit, control, reset } = useForm();

  const fileInputRef = useRef<any>(null);

  const [currentImage, setCurrentImage] = useState<any>(null);
  const [avatarFile, setAvatarFile] = useState<any>(null);

  const handleUpdateInputs = (inputs: IInput[]) => {
    setInputList(inputs);
  };

  const handleUpdateSocialTabs = (socialTabs: any[]) => {
    setSocialTabs(socialTabs);
  };

  const setCurrentTopic = (topicId: string) => {
    if (topicId === MOCK_TOPIC[0]) {
      navigate("/settings/account");
    } else {
      navigate("/settings/profile");
    }
  };

  const handleChangePhotoClick = () => {
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onSelectFile = (e: any) => {
    const file = e.target.files;
    const fileType = /^image\/(jpeg|png)$/;
    const maxFileSize = 1048576;

    if (!file || file.length === 0) {
      setAvatarFile(null);
      return;
    }

    if (!fileType.test(file[0].type)) {
      toast.error("Invalid file type. Please select a JPEG or PNG file.");
      return;
    }

    // eslint-disable-next-line no-magic-numbers
    if (file[0].size > maxFileSize) {
      toast.error("The file is too big, please upload a file smaller than 1 MB.");
      return;
    }

    setAvatarFile(file[0]);
  };

  const getUserInfo = () => {
    dispatch(fetchCurrentUser());
  };

  const onSubmit = (data: any) => {
    const SOCIAL_KEY_URL = "socialUrl";
    const socialValues: {[key: string]: string} = {};
    const socialInputList = inputList.filter((value, index, self) =>
      index === self.findIndex((t) => (
        t.socialKey === value.socialKey
      ))
    );

    // eslint-disable-next-line no-magic-numbers
    for (let i = 1; i < 17; i++) {
      const social = socialInputList.find(s => s.socialKey === `${SOCIAL_KEY_URL}${i}`);
      const socialVal = `${social?.placeholder}${social?.value}`;

      socialValues[`${SOCIAL_KEY_URL}${i}`] = social?.value && !social?.validMessage ? socialVal : "";
    }

    // eslint-disable-next-line
    const sendedData: IOperation[] = [
      { op: "add", path: PROFILE_UPDATE_FIELDS_PATHS.name, value: data.name },
      { op: "add", path: PROFILE_UPDATE_FIELDS_PATHS.description, value: data.description },
      { op: "add", path: PROFILE_UPDATE_FIELDS_PATHS.profile_url, value: data.profileUrl },
      { op: "add", path: PROFILE_UPDATE_FIELDS_PATHS.social, value: socialValues },
    ];

    dispatch(updateUserProfile(sendedData));

    if (avatarFile) {
      dispatch(updateProfilePhoto(avatarFile));
    }
  };

  useEffect(() => {
    if (!avatarFile) {
      setCurrentImage(null);
      return;
    }
    const objectUrl = URL.createObjectURL(avatarFile);
    setCurrentImage(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [avatarFile]);

  useEffect(() => {
    if (currentUser) {
      setCurrentImage(currentUser.profileImageUrl);
      reset({
        name: currentUser.name,
        description: currentUser.description || "",
        profileUrl: currentUser.profileUrl,
      });
    }
    // eslint-disable-next-line
  }, [currentUser])

  useEffect(() => {
    getUserInfo();
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const socials = socialAdapter(currentUser);
    const adaptInputs = socials.map(({ name, url, key, placeholder, isRequired, inputPlaceholder, validators }) => {

      let value;

      if (url.indexOf(placeholder) === 0) {
        value = url.replace(placeholder, "");
      } else {
        value = url.split("/").at(-1) || "";
      }

      return {
        id: uuidv4(),
        name,
        socialKey: key,
        value,
        validMessage: null,
        isRequired,
        placeholder,
        inputPlaceholder,
        validators,
      };
    });

    setInputList(adaptInputs);
  }, [currentUser]);

  useEffect(() => {
    if (!inputList.length) {
      return;
    }

    const inputNames = inputList.map(f => f.name);
    const updatedSocials = socialTabs.map(([keyValue, { isSelected, ...rest }]) => {
      if (inputNames.includes(keyValue)) {
        return [keyValue, { ...rest, isSelected: true }];
      }
      return [keyValue, { ...rest, isSelected }];
    });

    setSocialTabs(updatedSocials);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputList]);

  const renderSocialList = () => {
    return (
      <div className="ProfileSettingsPage-Socials">
        <div className="ProfileSettingsPage-SocialsTitle">Socials</div>
        <SocialLinksForm
          handleUpdateInputs={handleUpdateInputs}
          handleUpdateSocialTabs={handleUpdateSocialTabs}
          inputList={inputList}
          socialTabs={socialTabs}
          isProfile
        />
      </div>
    );
  };

  return (
    <Fragment>
      <Container className="wide page profileSettingsPage">
        <div className="TopHeader">
          <BackButton
            link={routes.user(currentUser.username)}
          />
          <Title>Settings</Title>
        </div>
        <TopMenu
          topicValues={MOCK_TOPIC}
          currentTopic={MOCK_TOPIC[1]}
          setCurrentTopic={setCurrentTopic}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <SectionAvatar>
            <input
              style={{ visibility: "hidden" }}
              type="file"
              accept="image/png, image/jpeg"
              ref={fileInputRef}
              onChange={onSelectFile}
            />
            <Avatar
              src={currentImage ? currentImage : default_user_image}
              sx={{
                width: "88px",
                height: "88px"
              }}
            />
            <Button
              onClick={handleChangePhotoClick}
              sx={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "14px",
                color: colors.white
              }}
            >
              Change profile photo
            </Button>
          </SectionAvatar>
          <Section sx={{
            "& p": {
              fontSize: "14px"
            }
          }}>
            <Label>Username</Label>
            <p>{currentUser.username}</p>
          </Section>
          <Section>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              rules={{
                required: {
                  value: true,
                  message: "Required Field!"
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomInput
                  value={value}
                  label={"Name"}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : ""}
                  fullWidth
                />
              )}
            />
          </Section>
          <Section>
            <Controller
              name="description"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomInput
                  value={value}
                  label={"Description"}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : ""}
                  fullWidth
                  multiline
                  rows={4}
                />
              )}
            />
          </Section>
          <Section>
            <Controller
              name="profileUrl"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomInput
                  value={value}
                  label={"Website"}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : ""}
                  fullWidth
                />
              )}
            />
          </Section>
          {currentUser.verified && (
            <Section>
              {renderSocialList()}
            </Section>
          )}
          <Button variant="contained" fullWidth sx={{
            background: colors.purpleGradient,
            margin: "2rem 0 0rem 0",
            minHeight: "3rem",
            borderRadius: "38px",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }} type="submit">
            <span>Save Changes</span>
          </Button>
        </form>
      </Container>

      <FooterAds/>
    </Fragment>
  );
};

const Container = styled("div")({
  maxWidth: sizes.containerSize,
  width: "100%",
  paddingTop: "27px",
  display: "flex",
  flexDirection: "column",
  marginBottom: "64px",
  "*": {
    fontFamily: "'Poppins', sans-serif!important"
  },
  ".TopHeader": {
    display: "flex",
    alignItems: "center",
    marginBottom: "18px",
    ".back svg": {
      width: "24px",
      height: "auto"
    }
  },
  "& form": {
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
    marginTop: "1rem"
  },
  "@media screen and (max-width: 767px)": {
    padding: "1rem",
    boxSizing: "border-box"
  }
});

const Title = styled("h3")({
  fontWeight: 500,
  fontSize: "20px",
  marginLeft: "8px"
});

const Section = styled("div")({
});

const SectionAvatar = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px"
});

const Label = styled("label")({
  fontWeight: 500,
  fontSize: "12px",
  color: colors.white,
  opacity: 0.8
});
